import Link from 'next/link';

interface ProjectHeaderProps {
  title: string;
  description: string;
  createdAt: string;
  updatedAt: string;
  status: 'active' | 'archived' | 'draft';
  author: string;
  subjects?: string[];
  tones?: string[];
  intents?: string[];
}

// Strip HTML tags from description for display
const stripHtmlTags = (html: string): string => {
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

export default function ProjectHeader({ 
  title, 
  description, 
  createdAt, 
  updatedAt, 
  status, 
  author,
  subjects = [],
  tones = [],
  intents = [],
}: ProjectHeaderProps) {
  const cleanDescription = description ? stripHtmlTags(description) : '';
  
  return (
    <header data-component-id="src/app/project-detail/components/ProjectHeader.tsx:43:4" data-component-path="src/app/project-detail/components/ProjectHeader.tsx" data-component-line="43" data-component-end-line="111" data-component-file="ProjectHeader.tsx" data-component-name="header" data-component-content="%7B%22elementName%22%3A%22header%22%2C%22className%22%3A%22mb-8%22%7D" className="mb-8">
      <h1 data-component-id="src/app/project-detail/components/ProjectHeader.tsx:44:6" data-component-path="src/app/project-detail/components/ProjectHeader.tsx" data-component-line="44" data-component-end-line="44" data-component-file="ProjectHeader.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22mb-4%20text-4xl%20font-bold%20text-gray-900%22%7D" className="mb-4 text-4xl font-bold text-gray-900">{title}</h1>
      {cleanDescription ? (
        <p data-component-id="src/app/project-detail/components/ProjectHeader.tsx:46:8" data-component-path="src/app/project-detail/components/ProjectHeader.tsx" data-component-line="46" data-component-end-line="48" data-component-file="ProjectHeader.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-gray-700%20max-w-3xl%20leading-relaxed%22%7D" className="text-gray-700 max-w-3xl leading-relaxed">
          {cleanDescription}
        </p>
      ) : null}

      <div data-component-id="src/app/project-detail/components/ProjectHeader.tsx:51:6" data-component-path="src/app/project-detail/components/ProjectHeader.tsx" data-component-line="51" data-component-end-line="90" data-component-file="ProjectHeader.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-4%20flex%20flex-wrap%20items-center%20gap-4%20text-sm%20text-gray-600%22%7D" className="mt-4 flex flex-wrap items-center gap-4 text-sm text-gray-600">
        {createdAt && (
          <time data-component-id="src/app/project-detail/components/ProjectHeader.tsx:53:10" data-component-path="src/app/project-detail/components/ProjectHeader.tsx" data-component-line="53" data-component-end-line="58" data-component-file="ProjectHeader.tsx" data-component-name="time" data-component-content="%7B%22elementName%22%3A%22time%22%2C%22textContent%22%3A%22Created%3A%22%7D" dateTime={createdAt}>
            Created: {new Date(createdAt).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
            })}
          </time>
        )}

        {updatedAt && updatedAt !== createdAt && (
          <time data-component-id="src/app/project-detail/components/ProjectHeader.tsx:62:10" data-component-path="src/app/project-detail/components/ProjectHeader.tsx" data-component-line="62" data-component-end-line="67" data-component-file="ProjectHeader.tsx" data-component-name="time" data-component-content="%7B%22elementName%22%3A%22time%22%2C%22textContent%22%3A%22Updated%3A%22%7D" dateTime={updatedAt}>
            Updated: {new Date(updatedAt).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
            })}
          </time>
        )}

        <span data-component-id="src/app/project-detail/components/ProjectHeader.tsx:70:8" data-component-path="src/app/project-detail/components/ProjectHeader.tsx" data-component-line="70" data-component-end-line="72" data-component-file="ProjectHeader.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22pill%20capitalize%20inline-flex%20items-center%20rounded-full%20bg-purple-100%20px-3%20py-1%20text-purple-800%22%7D" className="pill capitalize inline-flex items-center rounded-full bg-purple-100 px-3 py-1 text-purple-800">
          {status}
        </span>
        {author ? (
          <span data-component-id="src/app/project-detail/components/ProjectHeader.tsx:74:10" data-component-path="src/app/project-detail/components/ProjectHeader.tsx" data-component-line="74" data-component-end-line="88" data-component-file="ProjectHeader.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22By%22%7D">
            By{' '}
            {author === 'Ray Saltini' ? (
              <Link data-component-id="src/app/project-detail/components/ProjectHeader.tsx:77:14" data-component-path="src/app/project-detail/components/ProjectHeader.tsx" data-component-line="77" data-component-end-line="84" data-component-file="ProjectHeader.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22https%3A%2F%2Fwww.linkedin.com%2Fin%2Fraysaltini%2F%22%2C%22className%22%3A%22font-semibold%20hover%3Aunderline%20hover%3Atext-primary%20transition-colors%22%7D"
                href="https://www.linkedin.com/in/raysaltini/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold hover:underline hover:text-primary transition-colors"
              >
                {author}
              </Link>
            ) : (
              author
            )}
          </span>
        ) : null}
      </div>

      {(subjects.length || tones.length || intents.length) ? (
        <div data-component-id="src/app/project-detail/components/ProjectHeader.tsx:93:8" data-component-path="src/app/project-detail/components/ProjectHeader.tsx" data-component-line="93" data-component-end-line="109" data-component-file="ProjectHeader.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-wrap%20gap-2%20mt-4%22%7D" className="flex flex-wrap gap-2 mt-4">
          {subjects.map((subject) => (
            <span data-component-id="src/app/project-detail/components/ProjectHeader.tsx:95:12" data-component-path="src/app/project-detail/components/ProjectHeader.tsx" data-component-line="95" data-component-end-line="97" data-component-file="ProjectHeader.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22pill%20px-3%20py-1%20rounded-full%20bg-blue-100%20text-blue-800%22%7D" key={`subject-${subject}`} className="pill px-3 py-1 rounded-full bg-blue-100 text-blue-800">
              {subject}
            </span>
          ))}
          {tones.map((tone) => (
            <span data-component-id="src/app/project-detail/components/ProjectHeader.tsx:100:12" data-component-path="src/app/project-detail/components/ProjectHeader.tsx" data-component-line="100" data-component-end-line="102" data-component-file="ProjectHeader.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22pill%20px-3%20py-1%20rounded-full%20bg-yellow-100%20text-yellow-800%20capitalize%22%7D" key={`tone-${tone}`} className="pill px-3 py-1 rounded-full bg-yellow-100 text-yellow-800 capitalize">
              {tone}
            </span>
          ))}
          {intents.map((intent) => (
            <span data-component-id="src/app/project-detail/components/ProjectHeader.tsx:105:12" data-component-path="src/app/project-detail/components/ProjectHeader.tsx" data-component-line="105" data-component-end-line="107" data-component-file="ProjectHeader.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22pill%20px-3%20py-1%20rounded-full%20bg-yellow-100%20text-yellow-800%20capitalize%22%7D" key={`intent-${intent}`} className="pill px-3 py-1 rounded-full bg-yellow-100 text-yellow-800 capitalize">
              {intent}
            </span>
          ))}
        </div>
      ) : null}
    </header>
  );
}